/**
 * ratify — the sibling page's adapter (F7).
 *
 * Ratification is NOT a grant. Nothing persists, no counterparty receives
 * anything, and the verb is "do" rather than "may" — which is why it uses four
 * of the thirteen components and lives at its own route rather than as a mode
 * of the grant page. Forcing it in is what grew the old page four whole-page
 * escape hatches.
 *
 * TWO MODES, ONE PAGE. They are genuinely different acts and the old page named
 * them separately for good reason:
 *
 *   ratify           — change a node's STRATUM from inferred to declared. The
 *                      node becomes part of declared identity and apps can no
 *                      longer modify it directly. Nothing about its content
 *                      changes; what changes is who may change it.
 *   review_proposal  — apply proposed CONTENT changes to a node.
 *
 * WHY THE DIFF IS BUILT HERE. The current page renders proposed changes as
 * `JSON.stringify(changes, null, 2)` inside a <pre>. A person deciding whether
 * to accept a change to their own identity should not be reading a serialized
 * object — so this turns whatever shape the proposal carries into rows, and
 * falls back to showing the raw value rather than dropping anything it does not
 * recognise. An unreadable change is a question; a missing one is a lie.
 */

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const show = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(show).join(' · ');
  return JSON.stringify(v);
};

/**
 * Proposed changes → diff rows for C6.
 *
 * Accepts the shapes a proposal can carry:
 *   { field: { old, new } }   an explicit before/after
 *   { field: value }          a new value; `before` supplies the old one
 *   anything else             rendered as-is, never dropped
 *
 * @returns [{ field, from, to, changed }]
 */
export function proposalToDiff(changes, before = {}) {
  if (!isPlainObject(changes)) {
    return changes === undefined || changes === null
      ? []
      : [{ field: 'change', from: null, to: show(changes), changed: true }];
  }
  return Object.entries(changes).map(([field, value]) => {
    const explicit = isPlainObject(value) && ('old' in value || 'new' in value);
    const from = explicit ? value.old : before?.[field];
    const to = explicit ? value.new : value;
    return {
      field,
      from: from === undefined ? null : show(from),
      to: show(to),
      changed: show(from) !== show(to),
    };
  });
}

/** The stratum move, stated plainly. `null` when there is nothing to say. */
export function stratumChange(node) {
  const from = node?.stratum || 'inferred';
  if (from === 'declared') return null;      // already declared — ratifying is a no-op
  return { from, to: 'declared' };
}

/**
 * C10 rows. Ratification's terms are real terms — it changes who may edit a
 * node — which is itself the argument for a page rather than a modal.
 */
export function ratifyTerms(node, { proposedBy } = {}) {
  const rows = [];
  const move = stratumChange(node);
  if (move) {
    rows.push({
      label: 'Becomes',
      value: `Declared, not ${move.from}`,
      note: 'Part of your declared identity. Apps can no longer change it without asking you.',
    });
  }
  rows.push({
    label: 'Grants',
    value: 'Nothing',
    note: proposedBy
      ? `${proposedBy} receives no access from this. It only suggested it.`
      : 'No one receives access from this.',
  });
  rows.push({
    label: 'Recorded',
    value: 'As yours, from a proposal',
    note: proposedBy
      ? `Your ledger keeps ${proposedBy}'s name on the suggestion.`
      : 'Your ledger keeps where the suggestion came from.',
  });
  return rows;
}

/** C10 rows for applying content changes. */
export function reviewTerms({ proposedBy } = {}) {
  return [
    { label: 'Applies', value: 'To your graph, now',
      note: 'Version history keeps the previous value, so this is reversible.' },
    { label: 'Grants', value: 'Nothing',
      note: proposedBy ? `${proposedBy} receives no standing access.`
                       : 'No one receives standing access.' },
    { label: 'Recorded', value: 'As yours, from a proposal' },
  ];
}

/** The verb that actually happens — C11 never says "Submit". */
export const labelsFor = (mode) => (mode === 'ratify'
  ? { confirm: 'Declare it', deny: 'Not now' }
  : { confirm: 'Apply the change', deny: 'Discard' });
